import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/error')({
  component: ErrorPage,
});

function ErrorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get error message from URL parameters
  const { message } = Route.useSearch() as { message?: string };
  const errorMessage =
    message || t('errors.unexpected', 'An unexpected error occurred.');

  const handleClose = () => {
    // Try to close window, if it fails navigate back or to home
    try {
      window.close();
    } catch (error) {
      // Some browsers might throw security exceptions
      console.warn('Unable to close window programmatically:', error);
    }

    // Immediately navigate - this will only happen if window.close() failed
    // If window.history.length > 1, go back; otherwise go to dashboard
    if (window.history.length > 1) {
      // navigate({ to: -1 });
      window.history.back();
    } else {
      navigate({ to: '/dashboard' });
    }
  };

  return (
    <div
      className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4'
      role='alert'
      aria-live='assertive'
      aria-labelledby='error-title'
      aria-describedby='error-message'
    >
      <div className='max-w-md w-full bg-white p-8 rounded-lg shadow-sm border'>
        <div className='flex justify-center mb-4'>
          <div className='h-12 w-12 rounded-full bg-red-100 flex items-center justify-center text-red-500 text-2xl'>
            <span aria-hidden='true'>⚠️</span>
          </div>
        </div>
        <h1
          id='error-title'
          className='text-xl font-semibold text-center mb-2 text-red-600'
        >
          {t('evaluation.error.title', 'Resume Evaluation Failed')}
        </h1>
        <p id='error-message' className='text-gray-600 text-center mb-6'>
          {errorMessage}
        </p>
        <p className='text-gray-500 text-center text-sm mb-6'>
          {t(
            'evaluation.error.tryAgain',
            'Please try again or contact support if the problem persists.'
          )}
        </p>
        <div className='flex justify-center space-x-3'>
          <button
            onClick={handleClose}
            className='px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700 transition-colors'
          >
            {t('common.close', 'Close')}
          </button>
          <button
            onClick={() => navigate({ to: '/dashboard' })}
            className='px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-md text-white transition-colors'
          >
            {t('common.returnToDashboard', 'Return to Dashboard')}
          </button>
        </div>
      </div>
    </div>
  );
}
